
'use client';

import { useState, useRef } from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardContent, CardFooter } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Image as ImageIcon, Smile, Video, Tag, Loader2, X, Code, ThumbsUp, Star } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/context/AuthContext';
import Image from 'next/image';
import { Input } from './ui/input';
import { useCategory } from '@/context/CategoryContext';
import { usePosts } from '@/context/PostContext';
import { usePortfolio } from '@/context/PortfolioContext';
import type { User } from 'firebase/auth';
import type { Friend, Post } from '@/types';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';


const extractEmbedUrl = (input: string): string | null => {
    let match;

    // 1. Check for Sketchfab
    const sketchfabSrcRegex = /src="([^"]+sketchfab\.com\/models\/[^"]+\/embed[^"]*)"/;
    match = input.match(sketchfabSrcRegex);
    if (match && match[1]) return match[1];
    
    const sketchfabUrlRegex = /(?:https?:\/\/)?(?:www\.)?sketchfab\.com\/models\/([a-zA-Z0-9-]+)/;
    match = input.match(sketchfabUrlRegex);
    if (match && match[1]) {
        return `https://sketchfab.com/models/${match[1]}/embed?ui_theme=dark`;
    }

    // 2. Check for YouTube
    const youtubeShareRegex = /(?:https?:\/\/)?(?:www\.)?youtu\.be\/([a-zA-Z0-9_-]{11})/;
    match = input.match(youtubeShareRegex);
    if (match && match[1]) return `https://www.youtube.com/embed/${match[1]}`;
    
    const youtubeWatchRegex = /(?:https?:\/\/)?(?:www\.)?youtube\.com\/watch\?v=([a-zA-Z0-9_-]{11})/;
    match = input.match(youtubeWatchRegex);
    if (match && match[1]) return `https://www.youtube.com/embed/${match[1]}`;
    
    const youtubeEmbedRegex = /src="([^"]+youtube\.com\/embed\/[^"]+)"/;
    match = input.match(youtubeEmbedRegex);
    if (match && match[1]) return match[1];

    // 3. Check for Vimeo
    const vimeoEmbedRegex = /src="([^"]+player\.vimeo\.com\/video\/[^"]+)"/;
    match = input.match(vimeoEmbedRegex);
    if (match && match[1]) return match[1];

    const vimeoUrlRegex = /(?:https?:\/\/)?(?:www\.)?vimeo\.com\/(\d+)/;
    match = input.match(vimeoUrlRegex);
    if (match && match[1]) return `https://player.vimeo.com/video/${match[1]}`;

    return null;
}

interface CreatePostFormProps {
    author: User | Friend | null;
}

export function CreatePostForm({ author }: CreatePostFormProps) {
  // Determine which context to use based on the author type
  const isPortfolioPost = author && 'socials' in author; // Heuristic to check if author is a Friend
  const userPostsContext = usePosts();
  const portfolioPostsContext = usePortfolio();
  const { createPost } = isPortfolioPost ? portfolioPostsContext : userPostsContext;

  const { categories } = useCategory();
  const { toast } = useToast();
  const { user, isOwner } = useAuth();
  const [content, setContent] = useState('');
  const [category, setCategory] = useState<string>('');
  const [embedUrl, setEmbedUrl] = useState('');
  const [initialLikes, setInitialLikes] = useState(0);
  const [isVIP, setIsVIP] = useState(false);
  const [vipStyle, setVipStyle] = useState<Post['vipStyle']>('style1');
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      const newFiles = Array.from(files);
      setImageFiles(prev => [...prev, ...newFiles]);
      
      newFiles.forEach(file => {
        const reader = new FileReader();
        reader.onloadend = () => {
          setImagePreviews(prev => [...prev, reader.result as string]);
        };
        reader.readAsDataURL(file);
      })
    }
  };

  const removeImage = (index: number) => {
    setImageFiles(prev => prev.filter((_, i) => i !== index));
    setImagePreviews(prev => prev.filter((_, i) => i !== index));
     if(fileInputRef.current) {
        fileInputRef.current.value = "";
    }
  }

  const handleSubmit = async () => {
    const isContentEmpty = !content.trim();
    if ((isContentEmpty && imageFiles.length === 0 && !embedUrl) || isSubmitting) return;

    setIsSubmitting(true);

    try {
      let finalEmbedUrl: string | null = null;
      if (embedUrl.trim()) {
        finalEmbedUrl = extractEmbedUrl(embedUrl);

        if (!finalEmbedUrl) {
          toast({
            title: "Invalid Link",
            description: "The embed link could not be recognized. Please use a valid Sketchfab, YouTube, or Vimeo link.",
            variant: "destructive",
          });
          setIsSubmitting(false);
          return;
        }
      }
      
      await createPost({
        content,
        category: category || null,
        images: imageFiles as any,
        sketchfabEmbedUrl: finalEmbedUrl,
        initialLikes: initialLikes,
        isVIP: isVIP,
        vipStyle: isVIP ? vipStyle : undefined,
      }, author);

      // Reset form
      setContent('');
      setCategory('');
      setEmbedUrl('');
      setInitialLikes(0);
      setIsVIP(false);
      setVipStyle('style1');
      setImageFiles([]);
      setImagePreviews([]);
      if(fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (error) {
       console.error("Error creating post:", error);
       toast({
         title: "Post Error",
         description: "Your post could not be published.",
         variant: "destructive",
       });
    } finally {
        setIsSubmitting(false);
    }
  };
  
  if (!isOwner) {
    return null;
  }
  
  if (!author) {
    return null;
  }
  
  const authorName = 'displayName' in author ? author.displayName : author.name;
  const authorAvatar = 'photoURL' in author ? author.photoURL : author.avatarUrl;
  const isContentEmpty = !content.trim();

  return (
    <Card>
      <CardHeader className="p-4">
         <div className="flex items-start gap-3">
            <Avatar>
                <AvatarImage src={authorAvatar || "https://placehold.co/40x40.png"} alt={authorName || "User"} data-ai-hint="person portrait" />
                <AvatarFallback>{authorName?.split(' ').map(n=>n[0]).join('') || 'U'}</AvatarFallback>
            </Avatar>
            <Textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder={`What's new, ${authorName?.split(' ')[0] || ''}?`}
              className="flex-1 min-h-[80px]"
              disabled={isSubmitting}
            />
         </div>
      </CardHeader>
      <CardContent className="p-4 pt-0">
        {imagePreviews.length > 0 && (
            <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                {imagePreviews.map((preview, index) => (
                    <div key={index} className="relative">
                        <Image src={preview} alt={`Preview ${index + 1}`} width={150} height={150} className="rounded-lg object-cover w-full aspect-square" />
                        <Button size="icon" variant="destructive" className="absolute top-1 right-1 rounded-full h-6 w-6" onClick={() => removeImage(index)}>
                            <X className="h-4 w-4" />
                        </Button>
                    </div>
                ))}
            </div>
        )}
        
        <div className="space-y-4 mt-4">
             <Select value={category} onValueChange={setCategory} disabled={isSubmitting}>
                <SelectTrigger className="w-full bg-secondary border-none">
                    <div className="flex items-center gap-2 text-muted-foreground">
                        <Tag className="h-5 w-5" />
                        <SelectValue placeholder="Choose a category for your project" />
                    </div>
                </SelectTrigger>
                <SelectContent>
                    {categories.map((item) => (
                        <SelectItem key={item.id} value={item.name}>{item.name}</SelectItem>
                    ))}
                </SelectContent>
            </Select>

            <div className="flex items-center gap-2">
                <Code className="h-5 w-5 text-muted-foreground" />
                <Input
                    type="text"
                    value={embedUrl}
                    onChange={(e) => setEmbedUrl(e.target.value)}
                    placeholder="Paste URL or embed code (Sketchfab, YouTube, Vimeo)"
                    className="w-full bg-secondary border-none"
                    disabled={isSubmitting}
                />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="initial-likes" className="text-sm text-muted-foreground flex items-center gap-2">
                  <ThumbsUp className="h-4 w-4" />
                  <span>Initial Reaction Count</span>
                </Label>
                <Input
                  id="initial-likes"
                  type="number"
                  value={initialLikes}
                  onChange={(e) => setInitialLikes(parseInt(e.target.value, 10) || 0)}
                  className="w-full bg-secondary border-none"
                  disabled={isSubmitting}
                />
              </div>

               <div className="space-y-2">
                  <Label htmlFor="vip-switch" className="text-sm text-muted-foreground flex items-center gap-2">
                    <Star className="h-4 w-4 text-yellow-500" />
                    <span>VIP Post</span>
                  </Label>
                  <div className="flex items-center h-10">
                    <Switch
                      id="vip-switch"
                      checked={isVIP}
                      onCheckedChange={setIsVIP}
                      disabled={isSubmitting}
                    />
                  </div>
              </div>
            </div>

            {isVIP && (
              <div className="space-y-2">
                 <Label htmlFor="vip-style" className="text-sm text-muted-foreground">VIP Badge Style</Label>
                 <Select value={vipStyle} onValueChange={(v: Post['vipStyle']) => setVipStyle(v)} disabled={isSubmitting}>
                    <SelectTrigger id="vip-style" className="w-full bg-secondary border-none">
                        <SelectValue placeholder="Choose a badge style" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="style1">Japanese VFX Badge</SelectItem>
                        <SelectItem value="style2">Chinese Gold Badge</SelectItem>
                        <SelectItem value="style3">Bubble Ripple Badge</SelectItem>
                    </SelectContent>
                </Select>
              </div>
            )}
        </div>

        <Separator className="my-4" />
        <div className="grid grid-cols-3 gap-2">
          <Button variant="ghost" className="flex items-center justify-center gap-2" disabled>
            <Video className="h-5 w-5 text-red-500" />
            <span className="text-sm font-semibold text-muted-foreground">Video</span>
          </Button>
          <Button
            variant="ghost"
            className="flex items-center justify-center gap-2"
            onClick={() => fileInputRef.current?.click()}
            disabled={isSubmitting}
          >
            <ImageIcon className="h-5 w-5 text-green-500" />
            <span className="text-sm font-semibold text-muted-foreground">Photos</span>
            <input 
              type="file" 
              ref={fileInputRef} 
              className="hidden" 
              accept="image/*" 
              multiple
              onChange={handleImageChange}
            />
          </Button>
          <Button variant="ghost" className="flex items-center justify-center gap-2" disabled>
            <Smile className="h-5 w-5 text-yellow-500" />
            <span className="text-sm font-semibold text-muted-foreground">Feeling</span>
          </Button>
        </div>
      </CardContent>
      <CardFooter className="p-4 pt-0">
        <Button
          className="w-full"
          onClick={handleSubmit}
          disabled={(isContentEmpty && imageFiles.length === 0 && !embedUrl) || isSubmitting}
        >
          {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {isSubmitting ? 'Publishing...' : 'Publish'}
        </Button>
      </CardFooter>
    </Card>
  );
}
